import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import QuoteBuilderForm from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderForm.jsx';
import LayoutNavSectionList from 'components/layout_and_quote_builder/LayoutNavSectionList.jsx';
import MyEventListener from 'components/common/MyEventListener.jsx';
import PostRequest from 'components/common/PostRequest.jsx';
import { buildSetupWizardStages } from 'components/frank/utils/setupWizardStages.js';
import { linkedSectionIndexes } from 'components/frank/utils/linkedNavSection.js';
import { machineCount, hasMachineOfType, hasPricedMachineOfType, refreshCountsAndResolve } from 'components/frank/utils/machineSettings.js';
import { MaterialPriceStep, MachineEntryStep, MachinePriceStep } from 'components/frank/SetupStepForms.jsx';
import StandardManufacturingLinesToggle from 'components/frank/StandardManufacturingLinesToggle.jsx';

// The guided setup wizard for the /mcc (Frank) edit page -- a modal that hand-holds the user through
// configuring a quote, one decision per stage, so it is always obvious what to pick next:
//
//   Stage 1  Quantity in order (default 1000)
//   then, per priceable SECTION (e.g. Carton -- NOT per sub_section: one line + one solution per section):
//     pick a manufacturing line   (the filter panel + the manufacturing-line listing; a pick just MARKS it)
//     pick a solution             (the picked plan's solution cards; a click just MARKS one) -> Done on the last
//
// It REUSES the page's own per-section flow (ManufacturingLineLists, via LayoutNavSectionList) rather than
// rebuilding it: each section stage renders that component with wizardStage set, so all of its fetching,
// scoring and solution grouping is shared. The wizard only adds the chrome (stage title, "Stage X of N", a
// progress bar that fills as you advance, Back / Next / Done) and does the select-solution POST when it leaves
// a solution stage -- the same POST SelectedSolutionSections does on the normal page. Because that POST updates
// window.user_quote_nav_sections, closing the wizard reveals the normally-rendered, now-priced page -- and
// onClose reports the section it priced, so the page opens on that section's Pricing tab.
const FrankSetupWizard = ({
  navSections, config, onClose, manufacturingLinesSource, onManufacturingLinesSourceChange, machinesRevision
}) => {
  // Quantity in order, kept in step with the count field the quantity stage renders (it publishes
  // quote_builder.count_changed on change), so the solution cards in the section stages price against the
  // current quantity. Seeded from the quote, falling back to 1000 (the create default).
  const [count, setCount] = useState((window.user_quote && window.user_quote.count) || 1000);
  MyEventListener([
    { key: 'quote_builder.count_changed', callback: (message) => { setCount(() => message.detail); } }
  ]);

  // The stages, from the step manager: quantity, then per choosing nav section its MISSING own-lines setup steps
  // (material price, each machine and its price) followed by its line and solution. A linked section inherits its
  // line and solution from another, so the wizard never asks about it. Recomputed EVERY render from the live
  // setup state -- the server-seeded per-section requirements plus the live own counts -- so as a setup step is
  // completed it drops out and "Step X of N" shrinks to what is still left. On Standard lines there are no setup
  // steps (the example account has everything). See setupWizardStages and SectionSetupRequirements.
  const stages = buildSetupWizardStages(navSections, {
    linkedSectionIndexes: linkedSectionIndexes(window.user_quote_nav_sections),
    source: manufacturingLinesSource,
    requirementsBySection: window.frank_section_setup_requirements || {},
    hasMaterialPrice: (key) => machineCount(key) > 0,   // a material is a UserUnitPrice; its count is price-based
    // Type-aware: a machine of a type the section does NOT use does not satisfy the step (allowedTypes is the
    // row's allowed_types) -- so a carton owner with only a hard-cardboard die-cutter is still walked through it.
    hasMachine: (key, allowedTypes) => hasMachineOfType(key, allowedTypes),
    hasMachinePrice: (key, allowedTypes) => hasPricedMachineOfType(key, allowedTypes),
  });
  const totalStages = stages.length;

  const [currentStage, setCurrentStage] = useState(0);
  // Completing a setup step drops it, shrinking `stages` under the current index -- which is how the wizard
  // advances onto the next step. If the index ever ends up past the last stage (the tail shrank), pull it back.
  useEffect(() => {
    if (currentStage > totalStages - 1) { setCurrentStage(Math.max(0, totalStages - 1)); }
  }, [totalStages, currentStage]);
  const stageIndex = Math.min(currentStage, totalStages - 1);
  // A machine-price step's form shows no buttons of its own -- it hands its submit up here, and the wizard's Next
  // saves it (see the footer). savingSetupStep disables Next between the click and the save landing; it clears
  // when the step changes (a successful save advances the wizard) or when the save reports an error.
  const setupFormSubmitRef = useRef(null);
  const [savingSetupStep, setSavingSetupStep] = useState(false);
  // The material step opens on the price LIST and only becomes a save-with-Next step once its form leaves that
  // list for a create/copy/edit sub-form. The form reports its sub-state up here ('list' vs new/copy/edit), so
  // the footer knows whether to show Next (form open) or leave it a plain picker (list). Its cancel is handed up
  // too, so the wizard's Back returns to the list instead of leaving the stage. Reset to 'list' on every stage
  // change (below), so a freshly entered material stage never inherits the previous form's open state.
  const [materialFormState, setMaterialFormState] = useState('list');
  const materialFormCancelRef = useRef(null);
  // Take only the form OPENING from the form (a create/copy/edit sub-form). The CLOSE back to 'list' is driven by
  // the wizard itself -- goBack and the stage-change reset -- so the transient STATE_LIST the copy-from-system
  // re-seed briefly flashes (QuoteBuilderForm) can never collapse the footer to the picker mid-open.
  const handleMaterialFormState = useCallback((state) => {
    if (state !== 'list') { setMaterialFormState(state); }
  }, []);
  // Bumped when a setup step completes, to force the wizard to recompute its own stages. refreshCountsAndResolve
  // re-solves the whole page too (frank:own-machines-changed -> machinesRevision), but that page trigger can be
  // swallowed if a speculative fingerprint refresh is already in flight (FrankQuoteBuilderPage) -- so the wizard
  // does not depend on it: this local bump ALWAYS recomputes the steps and clears the Next spinner once the
  // fresh counts are in, whether or not the page re-solved.
  const [setupRevision, setSetupRevision] = useState(0);
  const resolveSetup = () => { refreshCountsAndResolve().finally(() => setSetupRevision((revision) => revision + 1)); };
  // The marked plan / marked solution PER SECTION, filed by section index. Reported up from the mounted
  // ManufacturingLineLists via the callbacks below; read back to enable Next / Done and to know what to POST.
  const [pickedBySection, setPickedBySection] = useState({});
  const [selectionBySection, setSelectionBySection] = useState({});
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState(false);

  // The section the wizard last PRICED -- persisted a solution for -- or null while it has priced none. Handed
  // to onClose so the page can open THAT section's Pricing tab: the user has just chosen a manufacturing line
  // and a solution, so what they want to see next is the price, not the manufacturing-line listing the page
  // would otherwise still be sitting on. Reported on every close, the "Done" and the "×" alike, since a user
  // who set up one section and dismissed the wizard has priced that section just the same.
  // A ref, not state: nothing renders from it, and the close handlers must read what the last persist wrote.
  const pricedSectionIndexRef = useRef(null);

  const stage = stages[stageIndex];
  const sectionIndex = stage.sectionIndex;
  // The stages only ever host the CURRENT section's component, so the picked/selection callbacks fire for the
  // current section. A ref carries that index into the (stable) callbacks, so they never re-create -- a
  // re-created callback would re-fire ManufacturingLineLists' notify effect every render.
  const sectionIndexRef = useRef(sectionIndex);
  sectionIndexRef.current = sectionIndex;

  const handlePicked = useCallback((identifier) => {
    setPickedBySection((previous) => ({ ...previous, [sectionIndexRef.current]: identifier }));
  }, []);
  const handleSelection = useCallback((group) => {
    setSelectionBySection((previous) => ({ ...previous, [sectionIndexRef.current]: group }));
  }, []);

  // The quantity stage renders ONLY the count field, not the whole general form (which also carries windows,
  // currency and the exchange table) -- the wizard asks one thing per stage. The trimmed config keeps the
  // general section's own post-on-change wiring, so the field persists and publishes exactly as on the Manage
  // tab.
  const quantityConfig = {
    ...config.general,
    attributes: config.general.attributes.filter((attribute) => attribute.name === 'count'),
  };

  const isLastStage = stageIndex === totalStages - 1;
  // The material step has BOTH faces: on its price list it is a PICKER (choosing a material carries it forward),
  // but once its create/copy/edit sub-form is open it behaves like the machine-price step -- the wizard's Next
  // SAVES that form. materialFormState (reported up by the form) is what tells the two apart.
  const materialFormOpen = stage.type === 'material' && materialFormState !== 'list';
  // A PICKER step (pick a material off its list / add a machine) is left by choosing an item, which re-solves and
  // drops the step -- so it shows no Next, only the wizard's Back (which cancels the step). A price-form step
  // keeps Next: there Next SAVES the form (the form has no Save of its own), then the save re-solves and advances.
  const isPickerStep = (stage.type === 'material' && !materialFormOpen) || stage.type === 'machine_entry';
  const isPriceFormStep = stage.type === 'machine_price' || materialFormOpen;
  // Back is disabled on the first stage (always the quantity stage), while a solution POST is in flight (posting),
  // and while a setup-step save is in flight (savingSetupStep) -- Backing out mid-save would cancel the form while
  // its create POST could still land and advance the wizard. (A material stage is never the first stage, so its
  // "Back returns to the list" while the sub-form is open is always available.)
  const backDisabled = stageIndex === 0 || posting || savingSetupStep;

  const canAdvance = () => {
    if (posting) { return false; }
    if (stage.type === 'quantity') { return true; }
    if (stage.type === 'line') { return Boolean(pickedBySection[sectionIndex]); }
    if (stage.type === 'solution') { return Boolean(selectionBySection[sectionIndex]); }
    // A price-form step's Next saves the form; keep it live except while a save is already in flight. (Picker
    // steps have no Next at all -- see the footer.)
    if (isPriceFormStep) { return !savingSetupStep; }
    return false;
  };

  // Save the current price-form step through the submit its form handed up, guarding against a double click while
  // a save is already running. The save's afterSave (refreshCountsAndResolve) re-solves, which drops this step
  // and advances the wizard; a failed save clears the flag via onSaveError so Next comes back.
  const submitSetupPrice = () => {
    if (savingSetupStep || !setupFormSubmitRef.current) { return; }
    setSavingSetupStep(true);
    setupFormSubmitRef.current();
  };

  // Persist + price ONE section from its marked solution -- the same select-solution POST
  // SelectedSolutionSections does on the normal page (updates window.user_quote_nav_sections and announces it),
  // so when the wizard closes the page shows the priced section.
  const persistSolution = (sectionIdx, group) => new Promise((resolve, reject) => {
    PostRequest({
      url: `/mcc/${window.user_quote_id}/select-solution/${sectionIdx}`,
      data: { lists: group },
      callback: (data) => {
        window.user_quote_nav_sections = data.window_nav_sections;
        window.dispatchEvent(new CustomEvent('navSectionsUpdated', {
          detail: { windowNavSections: data.window_nav_sections, sourceIndex: sectionIdx }
        }));
        resolve(data);
      }
    }).catch(reject);
  });

  // Closing always reports which section the wizard priced (null if it priced none), so the page can follow the
  // user to that section's Pricing tab.
  const closeWizard = () => { onClose({ pricedSectionIndex: pricedSectionIndexRef.current }); };

  const goNext = async () => {
    if (!canAdvance()) { return; }
    setPostError(false);

    // On a price-form step, Next SAVES the form; the save re-solves and drops the step, which is what advances
    // the wizard -- so do not step the index here.
    if (isPriceFormStep) { submitSetupPrice(); return; }

    // Leaving a solution stage prices that section before moving on (or closing).
    if (stage.type === 'solution') {
      setPosting(true);
      try {
        await persistSolution(sectionIndex, selectionBySection[sectionIndex]);
      } catch (error) {
        setPosting(false);
        setPostError(true);
        return; // keep the wizard open so the user can retry
      }
      pricedSectionIndexRef.current = sectionIndex;
      setPosting(false);
    }

    if (isLastStage) { closeWizard(); return; }
    setCurrentStage(stageIndex + 1);
  };

  const goBack = () => {
    if (backDisabled) { return; }
    setPostError(false);
    // With the material step's create/copy/edit sub-form open, Back returns to the frequently-used price list
    // (the form has no Cancel of its own) rather than leaving the stage. The wizard OWNS this 'list' transition
    // (the form's own 'list' reports are ignored -- see handleMaterialFormState), so it sets it here rather than
    // waiting on cancel()'s report; that also covers the rare window where the form is mid-remount and its
    // cancelRef is momentarily null, so Back still reverts the footer instead of doing nothing.
    if (materialFormOpen) {
      if (materialFormCancelRef.current) { materialFormCancelRef.current(); }
      setMaterialFormState('list');
      return;
    }
    setCurrentStage(stageIndex - 1);
  };

  // EVERY STAGE OPENS AT ITS OWN TOP. One scroll container (the body below) is reused by every stage, so the
  // place the reader had scrolled to was still there when the next stage drew into it: leaving the
  // manufacturing-line stage 840px down landed the solution cards 840px down as well, and Back landed on a
  // listing scrolled past its filters and its first row of cards. The wizard asks one question per stage --
  // each has to start where its answer starts.
  //
  // Nothing did this by accident either way: a next stage whose first frame was SHORTER than the body (the
  // solution stage while its solve was still out) came out at the top only because the browser clamps a scroll
  // position its content can no longer reach, and one whose cards were already in hand kept the old offset.
  //
  // The stage AND NOTHING ELSE is what re-runs this: the reader scrolls within a stage while picking, and a
  // reset on any other render would pull the list out from under that. Note ManufacturingLineLists' own
  // scroll-to-the-selection deliberately stands down in the wizard (#scrollToSelectedCard), so this is the only
  // hand on the modal's scroll position.
  //
  // Before paint (useLayoutEffect), so the new stage is drawn at the top rather than drawn where the last one
  // was left and then jumped.
  // Keyed on WHICH step is shown, not just the index: completing a setup step advances the wizard without the
  // index moving (the step drops from under it), so the body must still reopen at the top for the new step.
  const stageKey = `${stage.type}:${sectionIndex ?? ''}:${stage.machineKey ?? ''}`;
  const bodyRef = useRef(null);
  useLayoutEffect(() => {
    if (bodyRef.current) { bodyRef.current.scrollTop = 0; }
    // Every stage opens fresh: a new material stage starts on its price list, never inheriting the previous
    // material form's open sub-state (which lingers non-'list' until that form unmounts). Before paint, so the
    // footer is drawn as the plain picker rather than briefly showing Next over the new stage's loading form.
    setMaterialFormState('list');
  }, [stageKey]);
  // Clear an in-flight Next spinner on ANY of the ways a save resolves, so it can never hang:
  //   - the shown step changed (a landed save advanced the wizard; also how Back leaves a step) -- stageKey;
  //   - a setup step completed and the wizard recomputed -- setupRevision, bumped by resolveSetup once the fresh
  //     counts are in (independent of the page's own re-solve, which can be swallowed mid-refresh);
  //   - the page re-solved -- machinesRevision;
  //   - the save could not proceed at all (validation error, disabled_if, failed POST) -- onSaveError.
  useEffect(() => { setSavingSetupStep(false); }, [stageKey, machinesRevision, setupRevision]);

  // While the material step's create/copy/edit sub-form is open, tell the page to HOLD its speculative
  // focus/visibility remount until the form closes -- exactly as the inline empty-state adders do (see
  // ManufacturingLineLists and FrankQuoteBuilderPage's frank:own-setup-open / -close counting). The setup-step
  // frame is keyed on machinesRevision, so an un-held remount mid-edit would tear the half-entered form down AND
  // leave the footer thinking the form is open over the freshly-listed form (its Next would then submit nothing
  // and hang). The save that completes the step re-solves via own-machines-changed, which is EXEMPT from the hold,
  // so the step still drops and the wizard advances. Balanced: open on materialFormOpen, close on its clear or on
  // the wizard unmounting.
  useEffect(() => {
    if (!materialFormOpen) { return undefined; }
    window.dispatchEvent(new CustomEvent('frank:own-setup-open'));
    return () => window.dispatchEvent(new CustomEvent('frank:own-setup-close'));
  }, [materialFormOpen]);

  // THE SECTION NAME AS IT READS INSIDE THE SENTENCE. A section is labelled for a tab -- "Corrugated cardboard",
  // "Carton" -- where a capital is right; the English title then drops it MID-sentence ("Select a manufacturing
  // line for Corrugated cardboard"), where it is not.
  //
  // English only, and deliberately so: the Turkish title puts the section FIRST ("Oluklu mukavva için üretim
  // hattı seçin"), so there the capital is correct and lowering it would be the error. Restricting the change to
  // English also keeps this away from the Turkish dotted/dotless i, where a naive toLowerCase turns İ into i̇.
  //
  // Only the FIRST letter, and only when the second is already lower case: that leaves an initialism like "PVC"
  // alone rather than rendering it "pVC".
  const sectionInSentence = (label) => {
    if (window.locale !== 'en' || !label) { return label; }
    if (label.length > 1 && label[1] !== label[1].toLowerCase()) { return label; }

    return label[0].toLowerCase() + label.slice(1);
  };

  const stageTitle = () => {
    if (stage.type === 'quantity') { return t('setup_wizard.quantity_title'); }
    if (stage.type === 'material') {
      return t('setup_wizard.material_title', { section: sectionInSentence(stage.label) });
    }
    // The machine steps read per machine key -- "Add a printing machine" / "Add your printing machine price" --
    // so the two die-cutting keys read right too (setup_wizard.<machineKey>_title / _price_title).
    if (stage.type === 'machine_entry') { return t(`setup_wizard.${stage.machineKey}_title`); }
    if (stage.type === 'machine_price') { return t(`setup_wizard.${stage.machineKey}_price_title`); }
    if (stage.type === 'line') {
      return t('setup_wizard.line_title', { section: sectionInSentence(stage.label) });
    }
    return t('setup_wizard.solution_title', { section: sectionInSentence(stage.label) });
  };

  const renderStageBody = () => {
    if (stage.type === 'quantity') {
      return (
        <div style={{ maxWidth: '360px', margin: '0 auto' }}>
          <QuoteBuilderForm config={quantityConfig} />
        </div>
      );
    }

    // The own-lines setup steps -- add a material price, a machine, or a machine's price. Each is framed in the
    // same centred card and, on save, re-solves (SetupStepForms), which drops the satisfied step and advances the
    // wizard onto the next. Keyed by machinesRevision so a save that re-solves gives the next step a fresh mount.
    if (stage.type === 'material' || stage.type === 'machine_entry' || stage.type === 'machine_price') {
      return (
        <div key={`wizard_setup_${stageKey}_${machinesRevision}_${setupRevision}`} style={setupStepFrameStyle}>
          {stage.type === 'material' && (
            <MaterialPriceStep
              materialKey={stage.materialKey}
              submitRef={setupFormSubmitRef}
              cancelRef={materialFormCancelRef}
              onSaveError={() => setSavingSetupStep(false)}
              onFormStateChange={handleMaterialFormState}
              onResolved={resolveSetup}
            />
          )}
          {stage.type === 'machine_entry' && (
            <MachineEntryStep machineKey={stage.machineKey} allowedTypes={stage.allowedTypes} onResolved={resolveSetup} />
          )}
          {stage.type === 'machine_price' && (
            <MachinePriceStep
              machineKey={stage.machineKey}
              submitRef={setupFormSubmitRef}
              onSaveError={() => setSavingSetupStep(false)}
              onResolved={resolveSetup}
            />
          )}
        </div>
      );
    }

    // The section's own flow, scoped to this stage. Keyed by section so moving BETWEEN sections remounts (each
    // section restores its own picked plan from the URL and its own selection from the server); moving between
    // this section's line and solution stages keeps the same instance, so the picked plan and its solved
    // solutions carry over.
    return (
      <LayoutNavSectionList
        // Source in the key too, so switching it inside the wizard remounts and re-fetches this stage.
        key={`wizard_section_${sectionIndex}_${manufacturingLinesSource}_${machinesRevision}`}
        navSections={[navSections[sectionIndex]]}
        config={config}
        count={count}
        stageView={stage.type}
        inWizard
        onWizardPickedChange={handlePicked}
        onWizardSelectionChange={handleSelection}
      />
    );
  };

  // Progress fills as the user advances: empty on the first stage, full on the last. It is the modal's own top
  // edge (flush inside the card's rounded corners), so it reads as part of THIS popup rather than a second
  // detached bar.
  const progressPercent = totalStages > 1 ? (stageIndex / (totalStages - 1)) * 100 : 100;

  return createPortal(
    <div style={backdropStyle}>
      <div style={modalStyle} role="dialog" aria-modal="true">
        <div style={headerStyle}>
          <span style={{ gridColumn: 1, justifySelf: 'start', fontWeight: 600, fontSize: '15px', color: 'var(--ink, #10141a)' }}>
            {t('setup_wizard.title')}
          </span>
          {/* The setup progress lives WITH the stage indicator, centred, so it is part of this one popup rather
              than a separate bar. The slim bar under the text fills as the user advances. */}
          <div style={{ gridColumn: 2, justifySelf: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
            <span style={{ color: 'var(--ink-faint, #8a94a3)', fontSize: '13px' }}>
              {t('setup_wizard.stage_indicator', { current: stageIndex + 1, total: totalStages })}
            </span>
            <div style={{ width: '160px', height: '4px', background: 'var(--surface-muted, #f1f3f5)', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progressPercent}%`, background: 'var(--brand, #eb5424)', transition: 'width 200ms ease' }} />
            </div>
          </div>
          <button type="button" onClick={closeWizard} aria-label="Close" style={{ ...closeButtonStyle, gridColumn: 3, justifySelf: 'end' }}>×</button>
        </div>

        {/* Title and the quote-wide source control share ONE row: the title stays centered (equal flex spacers)
            and the control sits at the right, the same top-right spot as the /mcc header so users find it where
            they expect. The control is offered on the stages that list manufacturing lines; the quantity stage
            lists none, so it keeps the plain centered title. */}
        {stage.type === 'quantity' ? (
          <div style={titleStyle}>{stageTitle()}</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', padding: '18px 20px 6px' }}>
            <div style={{ flex: 1 }} />
            <div style={{ textAlign: 'center', fontSize: '18px', fontWeight: 600, color: 'var(--ink, #10141a)' }}>
              {stageTitle()}
            </div>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <StandardManufacturingLinesToggle
                value={manufacturingLinesSource}
                onChange={onManufacturingLinesSourceChange}
              />
            </div>
          </div>
        )}

        {/* The modal's one scrolling area -- see the stage reset above, which is what data-wizard-body names. */}
        <div ref={bodyRef} style={bodyStyle} data-wizard-body>{renderStageBody()}</div>

        {postError && (
          <div style={{ color: '#c00', fontSize: '13px', textAlign: 'center', padding: '0 20px 8px' }}>
            {t('setup_wizard.save_failed')}
          </div>
        )}

        <div style={footerStyle}>
          <button
            type="button"
            onClick={goBack}
            disabled={backDisabled}
            style={secondaryButtonStyle(backDisabled)}
          >
            {t('setup_wizard.back')}
          </button>
          {/* No Next on a PICKER step -- choosing a material / machine carries the wizard forward, so a Next
              would be a dead control. A price-form step keeps Next: there it SAVES the form (isPriceFormStep). */}
          {!isPickerStep && (
            <button
              type="button"
              onClick={goNext}
              disabled={!canAdvance()}
              style={primaryButtonStyle(!canAdvance())}
            >
              {(posting || savingSetupStep) ? '…' : (isLastStage ? t('setup_wizard.done') : t('setup_wizard.next'))}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const backdropStyle = {
  // Above the site's fixed top nav (its z-index would otherwise cover the modal header / stage indicator),
  // so the wizard is a true full-page modal. Centred both ways; the modal's maxHeight (90vh) plus the
  // backdrop's own overflow keeps the tall section stages from clipping when centred.
  position: 'fixed', inset: 0, zIndex: 100000, background: 'rgba(16, 24, 40, 0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4vh 2vw', overflow: 'auto',
};
const modalStyle = {
  position: 'relative', display: 'flex', flexDirection: 'column', width: '92vw', maxWidth: '1120px',
  maxHeight: '90vh', background: 'var(--surface, #ffffff)', borderRadius: '10px',
  boxShadow: '0 12px 48px rgba(16, 24, 40, 0.28)', overflow: 'hidden',
};
const headerStyle = {
  // A 1fr auto 1fr grid so the centre column (the stage indicator + progress) is truly centred on the modal,
  // regardless of the title / close-button widths -- space-between drifted it toward the narrower side.
  display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '16px',
  padding: '14px 20px', borderBottom: '1px solid var(--rule, #e3e7ec)',
};
const closeButtonStyle = {
  background: 'none', border: 'none', fontSize: '24px', lineHeight: 1, cursor: 'pointer',
  color: 'var(--ink-faint, #8a94a3)', padding: '0 4px',
};
const titleStyle = {
  padding: '18px 20px 6px', textAlign: 'center', fontSize: '18px', fontWeight: 600,
  color: 'var(--ink, #10141a)',
};
const bodyStyle = { flex: 1, overflow: 'auto', padding: '10px 20px 16px' };
// A setup step is just centred and capped at a readable width so it never stretches the full modal. It carries
// NO card of its own: each step's form is a QuoteBuilderForm table -- already a framed island -- and the machine
// picker frames itself, so a border here would only double up around them.
const setupStepFrameStyle = {
  width: '100%', maxWidth: '640px', margin: '0 auto', textAlign: 'left',
};
const footerStyle = {
  display: 'flex', justifyContent: 'center', gap: '16px',
  padding: '12px 20px', borderTop: '1px solid var(--rule, #e3e7ec)',
};
const buttonBase = {
  padding: '9px 22px', fontSize: '15px', fontWeight: 600, borderRadius: '8px', cursor: 'pointer',
};
const primaryButtonStyle = (disabled) => ({
  ...buttonBase, border: 'none',
  background: disabled ? 'var(--rule, #e3e7ec)' : 'var(--brand, #eb5424)',
  color: disabled ? 'var(--ink-faint, #8a94a3)' : '#ffffff',
  cursor: disabled ? 'default' : 'pointer',
});
const secondaryButtonStyle = (disabled) => ({
  ...buttonBase, background: 'var(--surface, #ffffff)', color: 'var(--ink-soft, #4e5865)',
  border: '1px solid var(--rule, #e3e7ec)', cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.5 : 1,
});

export default FrankSetupWizard;
